import { ShieldCheck } from 'lucide-react';
import { DevToolsEmptyState } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsEmptyState';
import { SipConfigRow } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/SipTab/SipConfigRow';
import { SipHealthHero } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/SipTab/SipHealthHero';
import {
  CARD_TITLE,
  SURFACE,
  TONE,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import { formatTimestamp } from 'Modules/softphone/components/SoftphoneDevTools/utils';
import { useSoftphoneStore } from 'Modules/softphone/store';
import React from 'react';
import { useShallow } from 'zustand/react/shallow';
import { cn } from '~/lib/utils';

/**
 * SipTab - Is registration healthy, and against what configuration
 */
export const SipTab: React.FC = () => {
  const { status, config, error, registrationAttempts, lastRegistrationTime } =
    useSoftphoneStore(
      useShallow((state) => ({
        status: state.status,
        config: state.config,
        error: state.error,
        registrationAttempts: state.registrationAttempts,
        lastRegistrationTime: state.lastRegistrationTime,
      }))
    );

  return (
    <div className="flex flex-col gap-3">
      <SipHealthHero
        status={status}
        registrationAttempts={registrationAttempts}
        lastRegistrationTime={lastRegistrationTime}
      />

      <div className={SURFACE.card}>
        <div className={cn(SURFACE.cardHeader, CARD_TITLE)}>
          Active configuration
        </div>

        {config ? (
          <>
            <SipConfigRow label="URI" value={config.uri} />
            <SipConfigRow label="Domain" value={config.domain} />
            <SipConfigRow label="Username" value={config.authorizationUsername} />
            <SipConfigRow
              label="Failed attempts"
              value={String(registrationAttempts)}
            />
            <SipConfigRow
              label="Last registration"
              value={formatTimestamp(lastRegistrationTime, {
                fallback: 'never',
                withDate: true,
              })}
            />
            <SipConfigRow
              label="Password"
              value={config.authorizationPassword}
              secret
            />
          </>
        ) : (
          <p className="px-3 py-3 text-[11.5px] text-zinc-400">
            No configuration loaded.
          </p>
        )}
      </div>

      <div className={SURFACE.card}>
        <div
          className={cn('flex items-center gap-2', SURFACE.cardHeader)}
        >
          <span className={CARD_TITLE}>Transport</span>
          <span
            className={cn(
              'rounded border px-[5px] py-0.5 font-sp-mono text-[10px]',
              status === 'registered' || status === 'connected'
                ? 'border-emerald-800 bg-emerald-950 text-emerald-400'
                : 'border-zinc-800 bg-zinc-900 text-zinc-400'
            )}
          >
            {status === 'registered' || status === 'connected'
              ? 'WSS OPEN'
              : 'WSS CLOSED'}
          </span>
        </div>
        {config && config.wsServers.length > 0 ? (
          config.wsServers.map((server) => (
            <div
              key={server}
              className="flex items-center gap-2.5 border-b border-zinc-800 px-3 py-[11px] last:border-b-0"
            >
              <span
                className={cn(
                  'h-1.5 w-1.5 flex-shrink-0 rounded-full',
                  status === 'registered' ? TONE.ok.dot : TONE.muted.dot
                )}
              />
              <span
                className="min-w-0 flex-1 truncate font-sp-mono text-[11.5px] text-zinc-200"
                title={server}
              >
                {server}
              </span>
            </div>
          ))
        ) : (
          <p className="px-3 py-3 text-[11.5px] text-zinc-400">
            No WebSocket server configured.
          </p>
        )}
      </div>

      {error ? (
        <div className={cn('p-3', TONE.bad.inset, 'rounded-[10px]')}>
          <h3 className="mb-1.5 text-[12.5px] font-semibold text-red-400">
            Error
          </h3>
          <pre className="whitespace-pre-wrap break-all font-sp-mono text-[11px] text-red-400">
            {error.message || String(error)}
          </pre>
          {error.stack && (
            <details className="mt-2">
              <summary className="cursor-pointer text-[11px] text-zinc-400 hover:text-zinc-300">
                Stack trace
              </summary>
              <pre className="mt-1 whitespace-pre-wrap break-all font-sp-mono text-[10px] text-zinc-400">
                {error.stack}
              </pre>
            </details>
          )}
        </div>
      ) : (
        <DevToolsEmptyState
          icon={<ShieldCheck className="h-[21px] w-[21px]" strokeWidth={1.7} />}
          tone="ok"
          title="No registration error"
          body="A failed REGISTER appears here with the error the client raised and the stack it came from."
          hint="the counter resets on every successful registration"
        />
      )}
    </div>
  );
};
